import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Milk, Pill, FlaskConical, Paintbrush, Wheat, Factory, Droplets, SprayCan } from "lucide-react";
import Reveal, { MaskedLine } from "../components/Reveal";
import PRODUCTS from "../data/products";
import POSTS from "../data/posts";

const IMAGES = {
  hero: "/facility/hero.jpg",
  factoryBottles: "/facility/chapter1.jpg",
  factoryConveyor: "/facility/chapter2.jpg",
  qualityLab: "/facility/chapter3.jpg",
};

const STATS = [
  ["27+", "Years of experience"],
  ["25+", "In-house moulds"],
  ["4,000+ KG", "Installed daily capacity"],
  ["100%", "Caps, handles & seals in-house"],
];

const MARQUEE_ITEMS = ["HDPE BOTTLES", "WIDE MOUTH CONTAINERS", "NARROW MOUTH MAUSERS", "FULL OPEN TOP DRUMS", "ROUND CONTAINERS", "FOOD-GRADE PACKS"];

const CHAPTERS = [
  {
    n: "01",
    kicker: "Who we are",
    title: "27 years of moulding trust",
    text: "Founded in 1999, SNP Plastech is built on one belief — packaging is not a container, it is the first handshake with your customer. From our works on G.T. Road, Greater Noida — right in the centre of NCR's industrial belt, with direct connectivity to Delhi, Noida and the Eastern Peripheral Expressway — every container, handle, cap and seal is manufactured in-house on our own blow-moulding and injection-moulding lines. Over 27 years we have grown from a single machine into a facility with an installed capacity of more than 4,000 kg per day — serving food, pharma, agro, chemical and paint brands that come to us for one reason: consistency. Same resin, same wall thickness, same seal — batch after batch, year after year.",
    img: IMAGES.factoryBottles,
    caption: "The works · G.T. Road, Greater Noida",
  },
  {
    n: "02",
    kicker: "What we craft",
    title: "From resin to retail-ready",
    text: "Food-grade HDPE bottles from 100 ml to 1 litre, wide-mouth containers up to 5 kg, and 20–65 litre mausers, round containers and full open-top drums — blow-moulded in-house, with handles, caps and seals injection-moulded on our own lines. Nothing outsourced.",
    img: IMAGES.factoryConveyor,
    caption: "Extrusion blow-moulding · Parison drop",
  },
  {
    n: "03",
    kicker: "How we assure quality",
    title: "Quality you can measure",
    text: "Every batch passes through in-house QA testing — dimensional checks, drop tests, torque and leak testing. Food-grade, FDA-compliant HDPE resins and full batch traceability come standard, not as an upgrade.",
    img: IMAGES.qualityLab,
    caption: "In-house injection moulding · Caps & closures",
  },
];

const GALLERY = [
  { src: "/facility/g1.jpg", caption: "Finished-goods godown" },
  { src: "/facility/g2.jpg", caption: "Blow-moulding line" },
  { src: "/facility/g3.jpg", caption: "Closures, ready" },
  { src: "/facility/g4.jpg", caption: "Injection moulding" },
  { src: "/facility/g5.jpg", caption: "FOT drums with handles" },
  { src: "/facility/g6.jpg", caption: "Dispatch staging" },
];

const INDUSTRIES = [
  { icon: Milk, name: "Food & Beverage", blurb: "Ghee, honey, edible-oil and syrup packs in food-grade HDPE." },
  { icon: Pill, name: "Pharma & Healthcare", blurb: "Food-grade HDPE bottles for syrups, tablets and powders." },
  { icon: Wheat, name: "Agriculture", blurb: "UV-stabilised mausers and cans for agro-chemicals." },
  { icon: FlaskConical, name: "Chemicals", blurb: "Narrow-mouth mausers and drums for solvents and chemicals." },
  { icon: Paintbrush, name: "Paints & Adhesives", blurb: "Full open-top drums and wide-mouth containers for paints, inks and adhesives." },
  { icon: Factory, name: "Industrial", blurb: "20–65 L mausers, round containers and drums for oils and bulk storage." },
  { icon: Droplets, name: "Lubricants & Oils", blurb: "Leak-proof HDPE packs for engine oils, greases and lubricants." },
  { icon: SprayCan, name: "Home Care", blurb: "Containers for detergents, cleaners and household chemicals." },
];

const QUALITY_CELLS = [
  { n: "01", title: "ZED Certified", text: "MSME Zero Defect Zero Effect certified." },  { n: "02", title: "Food-grade HDPE", text: "FDA-compliant, virgin food-grade HDPE resins only — no exceptions." },
  { n: "03", title: "In-house QA testing", text: "Drop, torque, leak and dimensional testing on every batch." },
  { n: "04", title: "Full traceability", text: "Every lot mapped from resin batch to dispatch document." },
];

const PROCESS = [
  { n: "01", title: "Requirement & sampling", text: "Share your drawing, sample or even a photo of what you need. We match it to our mould library or quote for new tooling." },
  { n: "02", title: "Resin & colour approval", text: "Food-grade HDPE resin and masterbatch are locked before production — you approve the colour and finish first." },
  { n: "03", title: "Production & QA", text: "Blow-moulded containers, injection-moulded caps, handles and seals — every batch drop, torque and leak tested in-house." },
  { n: "04", title: "Packing & dispatch", text: "Counted, poly-bagged, palletised and dispatched on schedule from our Greater Noida works, with full batch traceability." },
];

const Home = () => {
  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 900], [0, 180]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    const f = PRODUCTS.filter((p) => p.featured);
    const rest = PRODUCTS.filter((p) => !p.featured);
    setFeatured([...f, ...rest].slice(0, 4));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-obsidian">
        <motion.div style={{ y: heroImgY }} className="absolute inset-0">
          <video
            data-testid="hero-video"
            poster="/facility/hero-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-[120%] object-cover opacity-60"
          >
            <source src="/facility/hero-loop.mp4" type="video/mp4" />
            <source src="/facility/hero-loop.webm" type="video/webm" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/30" />
        </motion.div>

        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 lg:px-10 pt-40 pb-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="text-xs uppercase tracking-[0.25em] font-semibold text-bone/70 mb-8"
          >
            SNP Plastech · Plastic Packaging Manufacturer
          </motion.p>

          <h1 className="font-display font-semibold text-bone text-[15vw] sm:text-[12vw] lg:text-[9vw] leading-[0.92] tracking-tighter">
            <MaskedLine delay={0.2}>PRECISION</MaskedLine>
            <MaskedLine delay={0.32}>IN EVERY</MaskedLine>
            <MaskedLine delay={0.44}>
              POLYMER<span className="text-emerald">.</span>
            </MaskedLine>
          </h1>

          <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="text-bone/70 max-w-md text-base leading-relaxed"
            >
              Bottles, jars, containers and industrial packs — designed, moulded and delivered at scale for brands that refuse to compromise.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="flex flex-wrap items-center gap-4"
            >
              <Link
                to="/products"
                data-testid="hero-explore-btn"
                className="group inline-flex items-center gap-2 bg-emerald text-white font-medium text-sm px-7 py-3.5 rounded-full hover:bg-emerald-dark transition-colors duration-300"
              >
                Explore the Range
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-enquiry-btn"
                className="group inline-flex items-center gap-2 border border-bone/40 text-bone font-medium text-sm px-7 py-3.5 rounded-full hover:border-bone hover:bg-bone hover:text-obsidian transition-colors duration-300"
              >
                Start an Enquiry
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-14 pt-8 border-t border-bone/15 grid grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {STATS.map(([value, label]) => (
              <div key={label}>
                <p className="font-display font-medium text-3xl lg:text-4xl text-bone tracking-tight">{value}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-bone/50 mt-1">{label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section data-testid="marquee-section" className="bg-[#E5E5E0] border-b border-line py-7 overflow-hidden">
        <div className="flex w-max animate-marquee">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center gap-16 lg:gap-24 pr-16 lg:pr-24">
              {MARQUEE_ITEMS.map((item) => (
                <span key={`${copy}-${item}`} className="flex items-center gap-16 lg:gap-24">
                  <span className="font-display font-medium uppercase tracking-tight text-3xl lg:text-5xl text-obsidian/80 whitespace-nowrap">{item}</span>
                  <span className="w-3 h-3 bg-emerald inline-block shrink-0" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section id="about" data-testid="about-section" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-36">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mb-4">The Manifesto</p>
          <h2 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl leading-[1.02]">
            Built on scale. Obsessed with detail.
          </h2>
        </Reveal>

        <div className="mt-20 lg:mt-28 space-y-24 lg:space-y-32">
          {CHAPTERS.map((ch, i) => (
            <div key={ch.n} className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center border-t border-line pt-14">
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <Reveal>
                  <span className="font-display font-semibold text-7xl lg:text-8xl text-obsidian/10 tracking-tighter block leading-none">{ch.n}</span>
                  <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mt-6 mb-3">{ch.kicker}</p>
                  <h3 className="font-display font-medium text-3xl lg:text-4xl tracking-tight leading-[1.05]">{ch.title}</h3>
                  <p className="text-obsidian/60 leading-relaxed mt-5 max-w-md">{ch.text}</p>
                </Reveal>
              </div>
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <Reveal delay={0.1}>
                  <div className="group relative overflow-hidden border border-line">
                    <img
                      src={ch.img}
                      alt={ch.title}
                      className="w-full aspect-[16/10] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <p className="absolute bottom-4 left-4 text-[11px] uppercase tracking-[0.2em] text-bone/90 bg-obsidian/50 backdrop-blur-sm px-3 py-1.5">
                      {ch.caption}
                    </p>
                  </div>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INSIDE THE WORKS */}
      <section data-testid="facility-section" className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mb-4">Facility</p>
              <h2 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">Inside the works.</h2>
            </div>
            <p className="text-obsidian/60 max-w-sm text-sm leading-relaxed">
              Shot on our own floor — machines, moulds and material in motion at the Chhapraula works.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {GALLERY.map((g, i) => (
              <Reveal key={g.src} delay={(i % 3) * 0.08}>
                <div data-testid={`facility-tile-${i}`} className="group relative overflow-hidden border border-line">
                  <img
                    src={g.src}
                    alt={g.caption}
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <p className="absolute bottom-4 left-4 text-[11px] uppercase tracking-[0.2em] text-bone/90 bg-obsidian/50 backdrop-blur-sm px-3 py-1.5">
                    {g.caption}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED RANGE */}
      <section data-testid="featured-section" className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 lg:pb-36">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mb-4">Featured Range</p>
            <h2 className="font-display font-medium text-4xl sm:text-5xl tracking-tighter leading-[1.02]">Packs that perform.</h2>
          </div>
          <Link
            to="/products"
            data-testid="featured-view-all-link"
            className="group inline-flex items-center gap-2 text-sm font-medium border-b border-obsidian pb-1 hover:text-emerald hover:border-emerald transition-colors duration-200"
          >
            View full catalogue
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
          </Link>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08} className={i === 0 ? "sm:col-span-2" : ""}>
              <Link to="/products" data-testid={`featured-product-${p.slug}`} className="group block">
                <div className="relative overflow-hidden border border-line bg-white p-6">
                  <img
                    src={p.image}
                    alt={p.name}
                    className={`w-full object-contain transition-transform duration-700 ease-out group-hover:scale-105 ${i === 0 ? "aspect-[16/8]" : "aspect-[16/9]"}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                <div className="flex items-start justify-between gap-4 pt-4">
                  <div>
                    <h3 className="font-display font-medium text-xl tracking-tight group-hover:text-emerald transition-colors duration-200">{p.name}</h3>
                    <p className="text-xs uppercase tracking-[0.15em] text-obsidian/50 mt-1">{p.category} · {p.sizes.join(" / ")}</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 mt-1 shrink-0 text-obsidian/40 group-hover:text-emerald group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" strokeWidth={1.5} />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries" data-testid="industries-section" className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <Reveal className="mb-14">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mb-4">Industries Served</p>
            <h2 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-3xl">
              Every sector. One standard.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-line">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={(i % 3) * 0.08}>
                <div
                  data-testid={`industry-${ind.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group border-r border-b border-line p-8 lg:p-10 h-full hover:bg-obsidian transition-colors duration-300"
                >
                  <ind.icon className="w-8 h-8 text-emerald mb-6" strokeWidth={1.5} />
                  <h3 className="font-display font-medium text-2xl tracking-tight group-hover:text-bone transition-colors duration-300">{ind.name}</h3>
                  <p className="text-sm text-obsidian/60 group-hover:text-bone/60 mt-3 leading-relaxed transition-colors duration-300">{ind.blurb}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="process-section" className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <Reveal className="mb-14">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mb-4">How We Work</p>
            <h2 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-3xl">
              From enquiry to dispatch.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-line">
            {PROCESS.map((step, i) => (
              <Reveal key={step.n} delay={(i % 4) * 0.08}>
                <div data-testid={`process-step-${step.n}`} className="border-r border-b border-line p-8 lg:p-10 h-full">
                  <span className="font-display font-semibold text-6xl text-obsidian/10 tracking-tighter block leading-none">{step.n}</span>
                  <h3 className="font-display font-medium text-xl tracking-tight mt-6">{step.title}</h3>
                  <p className="text-sm text-obsidian/60 mt-3 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUALITY */}
      <section id="quality" data-testid="quality-section" className="bg-obsidian text-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <Reveal className="mb-14">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mb-4">Quality &amp; Compliance</p>
            <h2 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-3xl">
              Certified. Tested. Traceable.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
            {QUALITY_CELLS.map((cell, i) => (
              <Reveal key={cell.n} delay={i * 0.08}>
                <div data-testid={`quality-cell-${cell.n}`} className="bg-obsidian p-8 lg:p-10 h-full">
                  <span className="font-display font-semibold text-5xl text-bone/10 tracking-tighter block leading-none">{cell.n}</span>
                  <h3 className="font-display font-medium text-xl tracking-tight mt-6">{cell.title}</h3>
                  <p className="text-sm text-bone/50 mt-3 leading-relaxed">{cell.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VISION */}
      <section data-testid="vision-section" className="bg-obsidian text-bone border-t border-bone/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mb-4">Vision for the Future</p>
                <h2 className="font-display font-medium text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
                  Growing with the industry<span className="text-emerald">.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 space-y-0">
              {[
                ["A booming industry", "India's plastic packaging industry is already worth over $20 billion and grows every year — driven by food processing, pharma, agro-chemicals and e-commerce. Every one of those sectors packs in HDPE."],
                ["HDPE leads the pack", "As brands move away from glass, metal and multi-material packs, recyclable single-polymer HDPE is becoming the default choice — lighter, safer and cheaper to ship. That is the wave we ride."],
                ["Growing with it", "Since 1999 we have scaled with this industry — from one machine to a full blow + injection moulding facility. As the market grows, so will we: more lines, more capacity, more shapes."],
                ["New moulds, new markets", "We add moulds every year. Upcoming tooling targets the fastest-growing asks from our buyers — wider mouths, tamper-evident systems, export-grade finishes."],
              ].map(([title, text], i) => (
                <Reveal key={title} delay={i * 0.07}>
                  <div data-testid={`vision-item-${i}`} className="py-7 border-b border-bone/10 flex gap-6">
                    <span className="w-2 h-2 bg-emerald inline-block shrink-0 mt-2" />
                    <div>
                      <h3 className="font-display font-medium text-xl tracking-tight">{title}</h3>
                      <p className="text-sm text-bone/55 mt-2 leading-relaxed">{text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* JOURNAL TEASER */}
      <section data-testid="journal-teaser" className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mb-4">The Polymer Journal</p>
              <h2 className="font-display font-medium text-4xl sm:text-5xl tracking-tighter leading-[1.02] max-w-2xl">
                Want to understand plastics before you buy them?
              </h2>
            </div>
            <Link
              to="/blog"
              data-testid="journal-teaser-link"
              className="group inline-flex items-center gap-2 text-sm font-medium border-b border-obsidian pb-1 hover:text-emerald hover:border-emerald transition-colors duration-200"
            >
              Read the Journal
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {POSTS.slice(0, 2).map((post) => (
              <Reveal key={post.slug}>
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`journal-teaser-${post.slug}`}
                  className="group block border border-line bg-white p-8 lg:p-10 h-full hover:border-obsidian transition-colors duration-300"
                >
                  <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-emerald mb-4">
                    {post.category} · {post.readTime}
                  </p>
                  <h3 className="font-display font-medium text-2xl lg:text-3xl tracking-tight leading-tight group-hover:text-emerald transition-colors duration-200">
                    {post.title}
                  </h3>
                  <p className="text-sm text-obsidian/60 mt-4 leading-relaxed line-clamp-2">{post.excerpt}</p>
                  <span className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-obsidian/70 group-hover:text-emerald transition-colors duration-200">
                    Read article
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40 overflow-hidden">
        <motion.img
          src="/products/fot-white-ring.png"
          alt=""
          aria-hidden="true"
          data-testid="cta-drum-float"
          initial={{ opacity: 0, rotate: -8 }}
          whileInView={{ opacity: 1, rotate: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="hidden md:block absolute right-[2%] lg:right-[6%] bottom-0 w-[260px] lg:w-[340px] pointer-events-none"
        />
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mb-6">Have a packaging problem?</p>
        <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-[13vw] sm:text-[9vw] lg:text-[7vw]">
          <MaskedLine inView>LET&apos;S MOULD</MaskedLine>
          <MaskedLine inView delay={0.12}>
            THE ANSWER<span className="text-emerald">.</span>
          </MaskedLine>
        </h2>
        <Reveal delay={0.2} className="mt-12">
          <Link
            to="/contact"
            data-testid="cta-enquiry-btn"
            className="group inline-flex items-center gap-3 bg-obsidian text-bone font-medium px-9 py-4.5 rounded-full hover:bg-emerald transition-colors duration-300 py-4"
          >
            Start an Enquiry
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
